import {
    AddressType,
    AddressUse,
    AdmissionType,
    AlertNotificationMethod,
    AlertPriority,
    AppointmentReminderStatus,
    BirthdayTaskType,
    ContactLanguage,
    ContactMethod,
    ContactMethodLocation,
    ContactPointSystem,
    ContactPointUse,
    CoverageStatus,
    EncounterParticipantType,
    ExtendedMessageType,
    ExternalFacilityType,
    FacilityInviteTrigger,
    FacilityType,
    LookupTable,
    MessageStatus,
    MessageType,
    MessagingFeature,
    NameUse,
    NpsScoreGroup,
    PatientContactRelationship as PatientContactRelationshipEnum,
    PatientContactRole,
    PatientEventType,
    PatientInviteMethod,
    PatientSubscriptionTriggerType,
    PatientStatus,
    PatientVisitStatus,
    PractitionerRole,
    PreferredContactMethod,
    Role,
    ServiceType,
    SignatureDocumentFieldType,
    StaffTaskTriggerType,
    SubscriberRelationship,
    SystemContentType,
    SystemPlanType,
    WorkflowContentPromptTriggerType,
    WorkflowContentPromptType,
    WorkflowContentSegmentAlignment,
    WorkflowContentSegmentType,
    WorkflowContentType,
    WorkflowPlanSchedulingType,
    WorkflowPlanTriggerType,
    WorkflowPlanType, 
} from "@/store/enums";
import {Ref} from "vue";
import { TranslateResult } from "vue-i18n";

// API models

export class AdministrativeAddress {
    id?: number | null;
    patientDemographicsId?: number | null;
    addressUse: AddressUse;
    addressType: AddressType;
    line1: string;
    line2: string;
    city: string;
    state: string;
    district: string;
    postalCode: string;
    country: string;
}

export class AdministrativeContactPoint {
    id?: number | null;
    contactPointSystem: ContactPointSystem;
    contactPointUse: ContactPointUse;
    value: string;
    rank: number;

    constructor(contactPointSystem: ContactPointSystem, contactPointUse: ContactPointUse, value = '') {
        this.contactPointSystem = contactPointSystem;
        this.contactPointUse = contactPointUse;
        this.value = value;
    }
}

export class AdministrativePersonName {
    nameUse: NameUse;
    familyName = '';
    givenName = '';
    middleName = '';
    suffix = '';
}

export class Contact {
    id?: number;
    firstName: string;
    lastName: string;
    fullName: string;
    directContactAddressId?: number;
    directContactAddress?: ContactAddress;
    alertNotificationMethod: AlertNotificationMethod;
    contactLanguage: ContactLanguage;
    defaultConversationId?: number;
    defaultConversation?: Conversation;
    contactUserId?: number;
    contactUser?: ContactUser;
    emailAddress: string;
    phoneNumber: string;
    facilities: ContactFacility[];
    contactAddresses: ContactAddress[];
    isExternalContact: boolean;
    dateCreated: string;
    lastUpdated?: string;
    portalGuid?: string;
}

export class ContactAddress {
    id?: number;
    contactId?: number;
    address: string;
    contactMethod: ContactMethod;
    rank?: number;
    contactMethodLocation?: ContactMethodLocation;
    transportId?: number;
    transport?: Transport;
    responseTransportId?: number;
    dateInviteSent?: string;
    dateInviteToSend?: string;
    dateInviteReminderSent?: string;
    dateInviteAccepted?: string;
    dateInviteRejected?: string;
    dateDeleted?: string;
    printAddress?: string;
}

export class ContactGroupPatientAdmissionTag {
    id?: number;
    contactGroupId?: number;
    admissionTagId: number;
    admissionTag?: LookupTableEntry;
}

export class ContactGroup {
    ageRangeMax?: number;
    ageRangeMin?: number;
    autoPopulateGroup: boolean;
    contactGroupName: string;
    contactGroupSubscriptions: ContactGroupSubscription[];
    dateCreated: string;
    facilityId: number;
    facilityLocation?: Facility;
    facilityLocationId?: number;
    id?: number;
    lastUpdated?: string;
    membershipLastUpdated?: string;
    npsScoreGroup?: NpsScoreGroup;
    ownerId: number;
    patientAdmissionTags?: ContactGroupPatientAdmissionTag[];
    patientStatus?: PatientStatus;
    populateByAgeRange = false;
    populateByCaseTag = false;
    populateByFacilityLocation = false;
    populateByNpsScore = false;
    populateByPatientStatus = false;
    populateByServiceType = false;
    serviceType?: ServiceType;

}

export class ContactGroupSubscription {
    id?: number;
    contactGroupId: number;
    workflowPlanId: number;
    workflowPlan?: WorkflowPlan;
    facilityId: number;
    dateCreated: string;
    lastUpdated?: string;
}

export class ContactGroupMember {
    id?: number;
    contactGroupId: number;
    contactGroup?: ContactGroup;
    contactId: number;
    contact?: Contact;
    facilityId: number;
    dateCreated: string;
    lastUpdated?: string;
}

export class ContactFacility {
    id?: number;
    contactId: number;
    facilityId: number;
    facility?: Facility;
    facilityLocationId?: number;
}

export class ContactUser {
    id?: number;
    username: string;
    password: string;
    contactId?: number;
    inviteGuid: string;
    dateInvited: string;
    dateInviteCompleted: string;
    securityQuestion1Id?: number;
    securityQuestion1?: ContactUserSecurityQuestion;
    securityQuestion1Answer: string;
    securityQuestion2Id?: number;
    securityQuestion2?: ContactUserSecurityQuestion;
    securityQuestion2Answer: string;
    securityQuestion3Id?: number;
    securityQuestion3?: ContactUserSecurityQuestion;
    securityQuestion3Answer: string;
    dateLocked: string;
    lastLoginAttempt: string;
}

export class ContactUserSecurityQuestion {
    id?: number;
    securityQuestion: string;
}

export class Conversation {
    id?: number;
    conversationName: string;
    isPrivate: boolean;
    facilityId: number;
    dateCreated: string;
    lastUpdated?: string;
    messages: Message[];
    conversationMembers: ConversationMember[];
}

export class ConversationMember {
    id?: number;
    contactId: number;
    contact?: Contact;
    conversationId: number;
    conversation?: Conversation;
    patientEventAlertId?: number;
    lastReadDate?: string;
    dateCreated: string;
    lastUpdated?: string;
}

export class ExtendedMessage {
    id?: number;
    extendedBody: string;
    extendedMessageType: ExtendedMessageType;
    facilityId: number;
}

export class Facility {
    id?: number;
    externalFacilityType?: ExternalFacilityType;
    externalId?: number;
    facilityName: string;
    facilityType?: FacilityType;
    phoneNumber: string;
    state: string;
    timeZone: string;
    ianaTimeZoneName: string;
    clientSideTimeout: number;
    emailTransportId: number;
    emailTransport: Transport;
    twilioTransportId: number;
    twilioTransport: Transport;
    patientInviteMethod: PatientInviteMethod;
    featureSettings: FacilityFeatureSettings;
    morningMessagingHoursStart?: string;
    morningMessagingHoursEnd?: string;
    afternoonMessagingHoursStart?: string;
    afternoonMessagingHoursEnd?: string;
    facilityLogoMedia: Media;
    facilityLogoMediaId?: number;
    patientFacingFacilityName: string;
    patientFacingAddressLine1: string;
    patientFacingAddressLine2: string;
    patientFacingCity: string;
    patientFacingState: string;
    patientFacingPostalCode: string;
    patientFacingPhoneNumber: string;
    patientFacingFaxNumber: string;
    patientFacingURL: string;
    isActive: boolean;
    parentFacilityId?: number;
    facilityOwnerId?: number;
    dateCreated: string;
    dateSynced: string;
    lastUpdated?: string;
}

export class FacilityFeatureSettings {
    id: number;
    facilityId: number;
    configurableIntake: boolean;
    enableVisitReminders: boolean;
    enableVisitRequests: boolean;
    enableDigitalIntake: boolean;
    enablePortal: boolean;
    enableSurveys: boolean;
    enableTwilioRegistration: boolean;
    enableMessaging: boolean;
}

export class FacilityMessagingSettings {
    id: number;
    facilityId: number;
    facilityLocationId: number;
    facilityLocation?: Facility;
    facility: Facility;
    inviteTrigger: FacilityInviteTrigger = FacilityInviteTrigger.InitialEvaluationVisit;
    staffAssignedToCase = false;
    staffAssignedToPatientVisit = false;
    staffWhoEnabledPatientMessaging = false;
    messagingStartTime?: string;
    messagingEndTime?: string;
    byUser = false;
    followingStaff: { staffId?: number }[];
    dateCreated?: string;
}

export class FacilityIntegrationSettings {
    id: number;
    facilityId: number;
    facilityLocationId: number;
    facilityLocation?: Facility;
    facility: Facility;
    messagingFeature: MessagingFeature;
    accountSid: string;
    apiKey: string;
    apiUrl: string;
    dateCreated?: string;
}

export class FacilityReminderSettings {
    id: number;
    facilityId: number;
    facilityLocationId: number;
    facilityLocation?: Facility;
    remindHoursBefore: boolean;
    hours?: number;
    remindDaysBefore: boolean;
    days?: number;
    checkInMinutesBefore: boolean;
    checkInMinutes?: number;
    remindScheduled: boolean;
    remindRescheduled: boolean;
    dateCreated?: string;
}

export class LookupTableEntry {
    id?: number;
    lookupTableEntrySettingsId?: number;
    lookupTableEntrySettings: LookupTableEntrySettings;
    lookupTable: LookupTable;
    entryName: string;
    facilityId: number;
    facilityLocationId: number;
    facilityLocation?: Facility;
    duration?: number;
}

export class Media {
    id?: number;
    mediaGuid: string;
    mediaName: string;
    mediaContentType: string;
    mediaContent: any;
    facilityId: number;
}

export class Message {
    id?: number;
    sid: string;
    emailSubject: string;
    body: string;
    extendedMessageId?: number;
    extendedMessage?: ExtendedMessage;
    messageType: MessageType;
    status: MessageStatus;
    dateAcknowledged?: string;
    dateProcessed?: string;
    dateToProcess?: string;
    dateRecipientRead?: string;
    facilityId: number;
    conversationId?: number;
    conversation?: Conversation;
    senderId?: number;
    sender?: Contact;
    senderContactAddressId?: number;
    senderContactAddress?: ContactAddress;
    recipientId?: number;
    recipientContactAddressId?: number;
    recipientContactAddress?: ContactAddress;
    responsePrompt?: PatientPrompt;
    transportId?: number;
    dateCreated: string;
    lastUpdated?: string;
    workflowContentId?: number;
    workflowContent?: WorkflowContent;
    patientSubscriptionId?: number;
    patientSubscription?: PatientSubscription;
    messageGroupContacts?: Contact[];
    messageGroupContactGroups?: ContactGroup[];
    patientEvents?: PatientEvent[];
    patientVisit?: PatientVisit;
    isPatientEventsMessage?: boolean;
}

export class Patient extends Contact {
    status: PatientStatus;
    dateStatusUpdated?: string;
    contactRole: PatientContactRole;
    guardianFirstName: string;
    guardianLastName: string;
    dateOfBirth?: string;
    staffId?: number;
    netPromoterScore?: number;
    netPromoterScoreDate?: string;
    dateSetup?: string;
    dateIntakeRequested?: string;
    dateInitialSyncCompleted?: string;
    patientSettings?: PatientSettings;
    patientFeatureSettings?: PatientFeatureSettings[];
}

export class PatientAdmission {
    id?: number;
    patientId: number;
    admissionType: AdmissionType;
    parentAdmissionId?: number;
    serviceType?: ServiceType;
    facilityLocationId?: number;
    facilityLocation?: Facility;
    admitDate?: string;
    dischargeDate?: string;
    dischargeStatusId?: number;
    dischargeStatus?: LookupTableEntry;
    admissionTags?: PatientAdmissionTag[];
}

export class PatientAdmissionTag {
    id?: number;
    patientId: number;
    admissionTag?: LookupTableEntry;
}

export class PatientClinicalNote {
    id?: number;
    noteType: string;
    contentType: string;
    filename: string;
    dateCreated: string;
}

export class PatientContact {
    id?: number;
    patientDemographicsId?: number;
    patientCoverageId?: number;
    relationships: PatientContactRelationship[];
    relationshipDescription: string;
    nameId: number;
    name: AdministrativePersonName;
    birthDate?: string;
    telecom: AdministrativeContactPoint[];
    addressId: number;
    address: AdministrativeAddress;
    administrativeGenderId?: number;
    administrativeGender?: LookupTableEntry;
    periodStart?: string;
    periodEnd?: string;
}

export class PatientContactRelationship {
    id?: number;
    patientContactId?: number;
    relationship: PatientContactRelationshipEnum;
}

export class PatientCoverage {
    id?: number;
    patientDemographicsId?: number;
    insuranceName: string;
    policyNumber: string;
    groupNumber: string;
    status: CoverageStatus;
    order: number;
    relationship: SubscriberRelationship;
    relationshipOther: string;
    subscriber: PatientContact;
    key: string;
}

export class PatientDemographics {
    id?: number;
    patientId: number;
    birthDate?: string;
    names: AdministrativePersonName[];
    telecom: AdministrativeContactPoint[];
    addresses: AdministrativeAddress[];
    contacts: PatientContact[];
    coverage: PatientCoverage[];
    administrativeGenderId?: number;
    administrativeGender?: LookupTableEntry;
    genderIdentityId?: number;
    genderIdentity?: LookupTableEntry;
    genderIdentityOther?: string;
    sexualOrientationId?: number;
    sexualOrientation?: LookupTableEntry;
    sexualOrientationOther?: string;
    races: PatientDemographicsRace[];
    ethnicities: PatientDemographicsEthnicity[];
    tribalAffiliationId?: number;
    tribalAffiliation?: LookupTableEntry;
    preferredLanguageId?: number;
    preferredLanguage?: LookupTableEntry;
    preferredContactMethod?: PreferredContactMethod;
}

export class PatientDemographicsRace {
    raceId: number;
    race: LookupTableEntry;
    rank?: number;
}

export class PatientDemographicsEthnicity {
    ethnicityId: number;
    ethnicity: LookupTableEntry;
    rank?: number;
}

export class PatientEvent {
    id?: number;
    patientId: number;
    facilityLocationId?: number;
    facilityLocation?: Facility;
    patientEventType: PatientEventType;
    eventDate?: string;
    alertPriority?: AlertPriority;
    patientSubscriptionId?: number;
    patientSubscription?: PatientSubscription;
    visitId?: number;
    visit?: PatientVisit;
    admissionId?: number;
    admission?: PatientAdmission;
    messageId?: number;
    alertStaffId?: number;
    alertResolvedStaffId?: number;
    alertResolvedMessageId?: number;
    dateAlertNotified?: string;
    dateAlertRead?: string;
    dateAlertResolved?: string;
    dateCreated: string;
    lastUpdated?: string;
}

export class PatientFeatureSettings {
    id?: number;
    patientId: number;
    messagingFeature: MessagingFeature;
    featureGuid: string;
    dateRequested?: string;
    requestedByStaffId?: number;
    dateProcessed?: string;
    datePatientSent?: string;
    datePatientDeclined?: string;
    datePatientCompleted?: string;
    patientDemographicsId?: number;
    dateCreated: string;
    lastUpdated?: string;
    dateDeleted?: string;
}

export class PatientSettings {
    id?: number;
    patientId: number;
    enableEmailCampaigns: boolean;
    enableSurveys: boolean;
    dateCreated: string;
    lastUpdated?: string;
}

export class PatientPrompt {
    id?: number;
    patientId: number;
    workflowContentId?: number;
    workflowContent?: WorkflowContent;
    hasInvalidResponse?: boolean;
    hasResponseBody?: boolean;
    freeFormResponseValue?: string;
    booleanResponseValue?: boolean;
    dateResponseValue?: string;
    scalarResponseValue?: number;
    optionResponseValueId?: number;
    optionResponseValue?: WorkflowContentPromptOption;
    dateCompleted?: string;
    patientSubscriptionId?: number;
    promptMessageId?: number;
    responseMessageId?: number;
    dateCreated: string;
    lastUpdated?: string;
    mediaResponseMimeType?: string;
    mediaResponseValue?: string;
}

export class PatientSubscription {
    id?: number;
    patientId?: number;
    workflowPlanId: number;
    workflowPlan?: WorkflowPlan;
    contactGroupSubscriptionId: number;
    contactGroupSubscription?: ContactGroupSubscription;
    patientPrompts?: PatientPrompt[];
    dateStarted?: string;
    datePaused?: string;
    dateCompleted?: string;
    dateCreated: string;
    lastUpdated?: string;
}

export class PatientVisit {
    id?: number;
    patientId: number;
    patient?: Patient;
    admissionId?: number;
    caseId?: number;
    case?: PatientAdmission;
    facilityLocationId?: number;
    facilityLocation?: Facility;
    visitStatus: PatientVisitStatus;
    visitStatusReason: string;
    visitDate?: string;
    visitEndDate?: string;
    staffId?: number;
    staff?: Staff;
    eventId: number;
    event: PatientEvent;
    visitTypeId?: number;
    visitType?: LookupTableEntry;
    reminderStatus: AppointmentReminderStatus;
    dateReminderStatusUpdated?: string;
    appointmentRequestGuid?: string;
    participants: PatientVisitParticipant[];
    dateCreated: string;
    lastUpdated?: string;
}

export class PatientVisitParticipant {
    id?: number;
    patientId: number;
    staffId: number;
    staff?: Staff;
    participantType: EncounterParticipantType;
    practitionerRole: PractitionerRole;
}

export class PatientVisitSlotRequest {
    id?: number;
    patientId: number;
    slots: PatientVisitSlot[];
}

export class PatientVisitSlot {
    id?: number;
    patientVisitSlotRequestId?: number;
    facilityLocationId: number;
    staffId: number;
    startDate: string;
    endDate: string;
}

export class Report {
    id?: number;
    reportName: string;
    description: string;
    reportKey: string;
    dateCreated: string;
    lastUpdated?: string;
}

export class Staff extends Contact {
    staffSettings?: StaffSettings;
    staffRoles: StaffRole[];
}

export class StaffSettings {
    enablePushNotifications: boolean;
    enableDailyAlertsReport: boolean;
    birthdayTaskSettings: BirthdayTaskType;
    birthdayWorkflowContentId?: number;
    emailSignature: string;
}

export class StaffRole {
    staffId: number;
    role: Role;
}

export class Transport {
    id?: number;
    transportAddress: string;
    facilityId: number;
    active: boolean;
}

export class StaffTask {
    id?: number;
    taskDescription: string;
    patientId: number;
    patient?: Patient;
    patientEventId?: number;
    patientEvent?: PatientEvent;
    assignedStaffId?: number;
    resolvedStaffId?: number;
    dateDue?: string;
    dateAlertResolved?: string;
    dateCreated: string;
    lastUpdated?: string;
}

export class StaffReport {
    id?: number;
    reportName: string;
    reportFilters: string;
    reportId: number;
    report: Report;
    facilityId: number;
    staffId: number;
    dateCreated: string;
    lastUpdated?: string;
}

export class WorkflowContent {
    id?: number;
    systemContentType: SystemContentType;
    workflowContentName: string;
    workflowContentType: WorkflowContentType;
    emailSubject: string;
    patientSubscriptionTriggerType?: PatientSubscriptionTriggerType;
    integrationMessagingFeature: MessagingFeature
    subscriptionPlanId?: number;
    workflowContentPromptType?: WorkflowContentPromptType;
    requiredPrompt?: boolean;
    hiddenPrompt?: boolean;
    scalarPromptMin?: number;
    scalarPromptMinTitle?: string;
    scalarPromptMax?: number;
    scalarPromptMaxTitle?: string;
    workflowPlanId?: number;
    ownerId: number;
    facilityId: number;
    lookupTable?: LookupTable;
    workflowContentPromptOptions?: WorkflowContentPromptOption[];
    workflowContentPromptTriggers?: WorkflowContentPromptTrigger[];
    workflowContentVisitTypes?: WorkflowContentVisitType[];
    workflowContentSegments?: WorkflowContentSegment[];
    dateCreated: string;
    lastUpdated?: string;
    createdByStaffId?: number;
}

export class WorkflowContentPatientPrompt {
    workflowContent: WorkflowContent;
    patientPrompt: PatientPrompt;
}

export class WorkflowContentPromptOption {
    id?: number;
    optionSequence: number;
    value: string;
    title: string;
    workflowContentId: number;
    dateCreated: string;
    lastUpdated?: string;
}

export class WorkflowContentPromptTrigger {
    id?: number;
    workflowContentPromptTriggerType: WorkflowContentPromptTriggerType;
    workflowContentId: number;
    booleanTriggerValue?: boolean;
    scalarTriggerMinValue?: number;
    scalarTriggerMaxValue?: number;
    optionTriggerValueId?: number;
    optionTriggerValue?: WorkflowContentPromptOption;
    responseWorkflowContentId?: number;
    responseWorkflowContent?: WorkflowContent;
    workflowContentPromptTriggerTasks: WorkflowContentPromptTriggerTask[];
    tasksDescription?: string;
    dateCreated: string;
    lastUpdated?: string;
}

export class WorkflowContentPromptTriggerTask {
    id?: number;
    workflowContentPromptTriggerId?: number;
    staffId?: number;
    staff?: Staff;
    staffTaskTriggerType: StaffTaskTriggerType;
}

export class WorkflowContentSegment {
    id?: number;
    segmentType: WorkflowContentSegmentType;
    segmentSequence: number;
    segmentAlignment?: WorkflowContentSegmentAlignment;
    segmentLanguage?: ContactLanguage;
    body: string;
    imageMediaId?: number;
    imageMedia?: Media;
    workflowContentId?: number;
    dateCreated?: string;
    lastUpdated?: string;
}

export class WorkflowPlan {
    id?: number;
    systemPlanType?: SystemPlanType;
    hiddenPlan: boolean;
    workflowPlanName: string;
    workflowPlanType: WorkflowPlanType;
    schedulingType?: WorkflowPlanSchedulingType;
    isAdmissionWorkflow: boolean;
    workflowPlanContent: WorkflowPlanContent[];
    ownerId: number;
    facilityId: number;
    dateCreated?: string;
    lastUpdated?: string;
}

export class WorkflowPlanContent {
    id?: number;
    planSequence: number;
    workflowPlanTriggerType: WorkflowPlanTriggerType;
    triggerPeriod?: number;
    triggerDaysDelay?: number;
    workflowPlanId: number;
    workflowContentId: number;
    workflowContent?: WorkflowContent;
    dateCreated?: string;
    lastUpdated?: string;
}

export class WorkflowContentVisitType {
    id?: number;
    workflowContent?: WorkflowContent;
    workflowContentId?: number;
    visitTypeId: number;
    visitType?: LookupTableEntry
}

// View Model classes

export class ContactAuthorizationViewModel {
    lastName: string;
    dateOfBirth: string;
}

export class ContactGroupViewModel {
    contactGroup: ContactGroup;
    contactsCount: number;
}

export class ContactGroupSubscriptionViewModel {
    subscription: ContactGroupSubscription;
    contactsCount: number;
    totalDurationType: WorkflowPlanTriggerType;
    totalDuration: number;
}

export class CurrentUserViewModel {
    contact: Contact;
    facilityType: ExternalFacilityType;
    facilities: Facility[];
}

export class PortalFacilityDetailsViewModel {
    facility: PortalFacilityViewModel;
    facilityLocation: PortalFacilityViewModel;
    lookupTables: LookupTableViewModel[];
}

export class ContactUserAuthViewModel {
    username: string;
    password: string;
    securityQuestion1Id?: number;
    securityQuestion1Answer: string;
    securityQuestion2Id?: number;
    securityQuestion2Answer: string;
    securityQuestion3Id?: number;
    securityQuestion3Answer: string;
    intakeValidationCode?: string;
}

export class LookupTableViewModel {
    lookupTable: LookupTable;
    entries: LookupTableEntry[];
}

export class MessageAlertViewModel {
    patientEvent: PatientEvent;
    patient: Patient;
    messages: Message[];
}

export class MessageGroupViewModel {
    message: Message;
    contactGroupIds: number[];
    contactIds: number[];
}

export class PatientFeatureSettingsViewModel {
    patient: Patient;
    latestPatientDemographicsId?: number;
    latestDateRequested: string;
    latestDateAccessed: string;
    latestDateCompleted: string;
}

export class PatientInviteSummaryViewModel {
    sentInviteCount: number;
    pendingInviteCount: number;
    incompleteInviteCount: number;
    declinedInviteCount: number;
    intakeRequestCount: number;
}

export class PatientViewModel {
    patient: Patient;
    latestEvent: PatientEvent;
}

export class PatientInviteViewModel {
    patient: Patient;
    inviteStatus: string;
    inviteDate: string;
    patientVisitCount: number;
}

export class PortalFacilityViewModel {
    id?: number;
    facilityName: string;
    externalFacilityType?: ExternalFacilityType;
    phoneNumber: string;
    addressLine: string;
    logoGuid: string;
    ianaTimeZoneName: string;
}

export class WorkflowSurveyResponsesViewModel {
    workflowPlanId: number;
    patientPrompts: PatientPrompt[];
}

export class PatientIntakeViewModel {
    patientDemographics: PatientDemographics;
    directContactAddress: ContactAddress;
    workflowSurveys: WorkflowPlan[];
    previousSurveyResponses: WorkflowSurveyResponsesViewModel[];
    surveyResponses: PatientPrompt[];
}

export class PortalPatientIntakeViewModel extends PatientIntakeViewModel {
    patientId?: number;
    portalInviteGuid?: string;
    patientSettings: PatientSettings;
    messagingEnabled: boolean;
    remindersEnabled: boolean;
    dateIntakeRequested?: string;
    dateInviteAccepted?: string;
    coverageViewModels: CoverageViewModel[];
    securityQuestions: ContactUserSecurityQuestion[];
}

export class PortalPatientVisitIntakeViewModel {
    portalIntakeGuid: string;
    visit: PatientVisit;
    checkInCompleteContent: WorkflowContent;
}

export class PortalFacilityPatientVisitIntakeViewModel {
    portalIntakeGuid: string;
    visit: PatientVisit;
    validationCode: string;
    checkInCompleteContent: WorkflowContent;
}

export class QueryResultViewModel<T> {
    items: Array<T>;
    totalRows?: number;
}

export class PortalVisitCountViewModel {
    upcomingCount: number;
    requestedCount: number;
}

export class CoverageViewModel {
    coverage: PatientCoverage;
    images: FileData[];
}

export class FileData {
    data: string;
    filename: string;
}

export class PortalContactAddressViewModel {
    id?: number;
    contactId?: number;
    address: string;
    contactMethod: ContactMethod;
    rank?: number;
    contactMethodLocation?: ContactMethodLocation;
    dateInviteSent?: string;
    dateInviteAccepted?: string;
    dateInviteRejected?: string;
}

export class PortalPatientSettingsViewModel {
    patientSettings: PatientSettings;
    featureFlags: string[];
    messagingEnabled: boolean;
    remindersEnabled: boolean;
    directContactAddressId?: number;
    contactAddresses: PortalContactAddressViewModel[];
    facility: PortalFacilityViewModel;
    facilityLocation: PortalFacilityViewModel;
}

export class PortalVisitRequestViewModel {
    facilityLocations: Facility[];
    defaultFacilityLocationId?: number;
    staff: Staff[];
    defaultStaffId?: number;
    visitTypes: LookupTableEntry[];
}

export class PortalVisitSlotRequestViewModel {
    startDate?: string;
    endDate?: string;
    facilityLocationId?: number;
    staffId?: number;
    visitTypeId?: number;

    // currently unused
    daysOfWeek: number[];
    startTime?: string;
    endTime?: string;
}

export class StaffAlertSummaryViewModel {
    messageAlertCount: number;
    latestMessageAlertDate?: string;
    surveyAlertCount: number;
    latestSurveyAlertDate?: string;
    taskAlertCount: number;
    latestTaskDate?: string;
}

export class SurveyAlertViewModel {
    patientEvent: PatientEvent;
    patient: Patient;
}

export class SurveyViewModel {
    patientSubscription: PatientSubscription;
    surveyPrompts: string[];
    patientLanguage: ContactLanguage;
}

export class TaskAlertViewModel {
    task: StaffTask;
    patientEvent: PatientEvent;
    patient: Patient;
}

export class WorkflowPlanViewModel {
    workflowPlan: WorkflowPlan;
    contentCount: number;
    totalDuration: number;
    totalDurationType: WorkflowPlanTriggerType;
}

export class PaginationOptionsModel {
    itemsPerPage: number;
    pageIndex: number;
    totalItems?: number;
    itemsPerPageOptions?: number[];
    sortBy?: string[];
    sortDesc?: boolean[];
}

export class PagingOptionsModel {
    rows: number;
    page: number;
    totalRecords?: number;
    sortField?: string;
    sortOrder?: number;
}

export class VisitRequestViewModel {
    slotId: number;
    reasonScheduled: string;
    authorization: ContactAuthorizationViewModel;
}

// UI classes

export interface SelectItem {
    value: number | string;
    text: string;
}

export interface NavigateSelectItem {
    value: number | string;
    text: string;
    routeName: string;
    patient?: Patient;
}

export interface HeaderSelectItem {
    header: string;
}

export interface DividerSelectItem {
    divider: boolean;
}

export interface Alert {
    type: string;
    text: string | TranslateResult;
}

export interface ConfirmModal {
    modalName?: string;
    title: string;
    message: string;
    showCancel?: boolean;
    hideNoButton?: boolean;
}

export interface Modal {
    name: string;
    props?: any;
    promise?: Promise<any>;
}

export interface Filters {
    startDate?: string;
    endDate?: string;
    facilityLocationIds?: number[];
    staffIds?: number[];
}

export interface JwtPayload {
    value: string;
    username: string;
    appName: string;
    tokenExpiration: Date;
}

export interface ContactFormConfig {
    relationship: { maxlength: number };
    givenName: { maxlength: number };
    familyName: { maxlength: number };
    nickname: { maxlength: number };
    middleName: { maxlength: number };
    suffix: { maxlength: number };
    addressLine1: { maxlength: number };
    addressCity: { maxlength: number };
    addressZipCode: { maxlength: number };
}

export interface IntakeFormConfig {
    patient: ContactFormConfig;
    patientDemographics: {
        otherSexualOrientation: { maxlength: number };
        otherGenderIdentity: { maxlength: number };
    };
    coverage: {
        insuranceName: { maxlength: number };
        policyNumber: { maxlength: number };
        groupNumber: { maxlength: number };
    };
    coverageSubscriber: ContactFormConfig;
    spouse: ContactFormConfig;
    guarantor: ContactFormConfig;
    emergencyContact: ContactFormConfig;
    previousContact: ContactFormConfig;
}

export class IntakePatientDemographicsModel {
    patientName: AdministrativePersonName = new AdministrativePersonName();
    patientNickname: AdministrativePersonName = new AdministrativePersonName();
    patientAddress: AdministrativeAddress = new AdministrativeAddress();
    patientDateOfBirth?: string;
    patientAdministrativeGenderId?: number;
    patientAdministrativeGender?: LookupTableEntry;
    patientGenderIdentityId?: number;
    patientGenderIdentity?: LookupTableEntry;
    patientGenderIdentityOther?: string = '';
    patientSexualOrientationId?: number;
    patientSexualOrientation?: LookupTableEntry;
    patientSexualOrientationOther?: string = '';
    patientPrimaryRaceId?: number;
    patientPrimaryRace?: LookupTableEntry;
    patientPrimaryEthnicityId?: number;
    patientPrimaryEthnicity?: LookupTableEntry;
    patientTribalAffiliationId?: number;
    patientTribalAffiliation?: LookupTableEntry;
    patientPreferredLanguageId?: number;
    patientPreferredLanguage?: LookupTableEntry;
}

export class LookupTableEntrySettings {
    duration?: number;
    isVisible: boolean;
    patientFacingLabel: string;
    isFacilityDefault: boolean;
}

export class NpsReportDataViewModel {
    detractors: number;
    passive: number;
    promoters: number;
    netPromoterScore: number|null;
}

export class NpsReportSeriesViewModel {
    name: 'Detractors' | 'Passive' | 'Promoters';
    data: {
        [date: string]: number
    }
}

export class NpsReportViewModel {
    overallScore: NpsReportDataViewModel;
    overallPreviousScore: NpsReportDataViewModel;
    scoresByMonth: NpsReportSeriesViewModel[];
}

export class NpsReportPatientViewModel {
    patient: Patient;
    patientPrompt: PatientPrompt;
}

export class MessageTrafficReportSummaryViewModel {
    smsContactMethodCount: number;
    emailContactMethodCount: number;
    currentTotalCount: number;
    previousTotalCount: number;
}

export class MessageTrafficReportSeriesViewModel {
    name: 'Sent' | 'Received';
    data: {
        [date: string]: number
    }
}

export class MessageTrafficReportViewModel {
    contactMethodSummary: MessageTrafficReportSummaryViewModel;
    messagesByMonth: MessageTrafficReportSeriesViewModel[];
}

export class RegisterRateReportViewModel {
    statusSummary: RegisterReportSummaryViewModel;
    statusByMonth: RegisterRateReportStatusSeriesViewModel[];
}

export class RegisterReportSummaryViewModel {
    acceptedCount: number;
    declinedCount: number;
    openCount: number;
    currentTotalCount: number;
    previousTotalCount: number;
}

export class RegisterRateReportStatusSeriesViewModel {
    name: 'Accepted' | 'Declined' | 'Open';
    data: {
        [date: string]: number
    }
}

export class ReminderTrafficReportViewModel {
    statusSummary: ReminderTrafficReportSummaryViewModel;
    statusByMonth: ReminderTrafficReportStatusSeriesViewModel[];
}

export class ReminderTrafficReportSummaryViewModel {
    sentCount: number;
    confirmedCount: number;
    currentTotalCount: number;
    previousTotalCount: number;
}

export class ReminderTrafficReportStatusSeriesViewModel {
    name: 'Sent' | 'Confirmed';
    data: {
        [date: string]: number
    }
}

export class SignatureDocumentViewModel {
    fields: {type: SignatureDocumentFieldType, value: Ref<string|undefined>}[];
    required: boolean;
}

export class SignatureData {
    imgSrc: string;
    text: string;
}
